import { ref } from 'vue';

const isKeyboardVisible = ref(false);
const targetInput = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);
const cursorPosition = ref<{ start: number; end: number }>({ start: 0, end: 0 });
const originalInputType = ref<string | null>(null);

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

// Types d'input qu'on peut temporairement convertir en 'text' pour supporter setSelectionRange
const CONVERTIBLE_TYPES = ['email', 'number'];

// Convertit temporairement l'input en type text pour supporter la sélection
const convertToTextType = (input: HTMLInputElement): void => {
  if (CONVERTIBLE_TYPES.includes(input.type)) {
    originalInputType.value = input.type;
    input.type = 'text';
  }
};

// Restaure le type original de l'input
const restoreOriginalType = (input: HTMLInputElement): void => {
  if (originalInputType.value) {
    input.type = originalInputType.value;
    originalInputType.value = null;
  }
};

export function useKeyboard() {

  // Sauvegarde la position actuelle du curseur
  const saveCursorPosition = () => {
    if (targetInput.value) {
      const input = targetInput.value;
      cursorPosition.value = {
        start: input.selectionStart ?? input.value.length,
        end: input.selectionEnd ?? input.value.length,
      };
    }
  };

  const setTargetInput = (input: HTMLInputElement | HTMLTextAreaElement) => {
    if (isMobile) return;

    // Si c'est un input avec un type non supporté, le convertir en text
    if (input instanceof HTMLInputElement && CONVERTIBLE_TYPES.includes(input.type)) {
      convertToTextType(input);
    }

    targetInput.value = input;
    isKeyboardVisible.value = true;

    // Initialiser la position du curseur à la fin
    cursorPosition.value = {
      start: input.value?.length ?? 0,
      end: input.value?.length ?? 0,
    };

    // Sauvegarder la position à chaque interaction avec l'input
    input.addEventListener('keyup', saveCursorPosition);
    input.addEventListener('mouseup', saveCursorPosition);
    input.addEventListener('touchend', saveCursorPosition);
    input.addEventListener('input', saveCursorPosition);
  };

  const showKeyboard = () => {
    if (isMobile) return;
    if (targetInput.value) {
      isKeyboardVisible.value = true;
    }
  };

  const hideKeyboard = () => {
    isKeyboardVisible.value = false;

    // Restaurer le type original quand on ferme le clavier
    if (targetInput.value && targetInput.value instanceof HTMLInputElement) {
      restoreOriginalType(targetInput.value);
    }
  };

  const clearTargetInput = () => {
    if (targetInput.value) {
      const input = targetInput.value;
      input.removeEventListener('keyup', saveCursorPosition);
      input.removeEventListener('mouseup', saveCursorPosition);
      input.removeEventListener('touchend', saveCursorPosition);
      input.removeEventListener('input', saveCursorPosition);

      // Restaurer le type original
      if (input instanceof HTMLInputElement) {
        restoreOriginalType(input);
      }
    }
    isKeyboardVisible.value = false;
    targetInput.value = null;
    cursorPosition.value = { start: 0, end: 0 };
  };

  // Appelé par le clavier virtuel sur mousedown/touchstart pour figer la position
  const onKeyboardInteractionStart = () => {
    if (targetInput.value) {
      const input = targetInput.value;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      if (start !== null && end !== null) {
        cursorPosition.value = { start, end };
      }
    }
  };

  const onKeyPress = (key: string) => {
    const input = targetInput.value;
    if (!input) return;

    const currentValue = input.value || '';

    let start = cursorPosition.value.start;
    let end = cursorPosition.value.end;

    // Validation des positions
    start = Math.max(0, Math.min(start, currentValue.length));
    end = Math.max(0, Math.min(end, currentValue.length));

    // Ignorer les touches de fonction
    if (['{shift}', '{lock}', '{symbols}', '{abc}', '{escape}'].includes(key)) {
      input.focus();
      input.setSelectionRange(start, end);
      return;
    }

    let newValue: string;
    let newPosition: number;

    if (key === '{bksp}') {
      if (start === end) {
        if (start > 0) {
          newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
          newPosition = start - 1;
        } else {
          return;
        }
      } else {
        newValue = currentValue.slice(0, start) + currentValue.slice(end);
        newPosition = start;
      }
    } else {
      const char = key === '{space}' ? ' ' : key;

      // Si l'input possède l'attribut data-numeric-only, on bloque les caractères non autorisés
      if (input.dataset.numericOnly === 'true') {
        if (!/^[0-9.,]$/.test(char)) {
          // Si ce n'est pas un chiffre, un point ou une virgule, on ignore la frappe du clavier virtuel
          input.focus();
          input.setSelectionRange(start, end);
          return;
        }
      }
      newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
      newPosition = start + char.length;
    }

    // Mettre à jour la valeur
    input.value = newValue;

    // Mettre à jour la position sauvegardée
    cursorPosition.value = { start: newPosition, end: newPosition };

    // Déclencher l'événement input pour v-model
    input.dispatchEvent(new Event('input', { bubbles: true }));

    // Restaurer le focus et positionner le curseur
    input.focus();
    input.setSelectionRange(newPosition, newPosition);
  };

  return {
    isKeyboardVisible,
    targetInput,
    isMobile,
    setTargetInput,
    showKeyboard,
    hideKeyboard,
    clearTargetInput,
    onKeyPress,
    onKeyboardInteractionStart,
  };
}

